import { customerService, extractAssetQuantity } from "../../../services/customerService";

export const NO_AMC_TYPE = "No AMC";

export function toAmcTypeDisplay(rawValue) {
  return String(rawValue || "").trim().replace(/_/g, " ");
}

export function isNoAmcType(asset = {}) {
  const raw = asset.amcType || asset.amc_type || asset.AMCType || asset.amctype || "";
  return toAmcTypeDisplay(raw) === NO_AMC_TYPE;
}

export function toDateInputValue(value) {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

export function customerRecordId(customer) {
  return customer?.id || customer?.customerId || customer?.customer_id || "";
}

export function customerLabel(customer) {
  const id = customerRecordId(customer);
  return customer?.companyName || customer?.company_name || (id ? `Customer #${id}` : "Customer");
}

export function locationRecordId(location) {
  return location?.locationId || location?.id || location?.location_id || "";
}

export function locationLabel(location) {
  const id = locationRecordId(location);
  return location?.locationName || location?.location_name || (id ? `Location ${id}` : "Location");
}

export function assetRecordId(asset) {
  return asset?.id || asset?.assetId || asset?.asset_id || "";
}

export function emptyAsset(defaultLocationId = "") {
  return {
    locationId: defaultLocationId ? String(defaultLocationId) : "",
    assetName: "",
    assetCategory: "",
    manufacturer: "",
    modelNumber: "",
    serialNumber: "",
    invoiceNumber: "",
    invoiceDate: "",
    installDate: "",
    warrantyType: "",
    warrantyStartDate: "",
    warrantyExpiry: "",
    warrantyEndDate: "",
    amcType: "",
    amcOrder: "",
    amcOrderDate: "",
    tenantAssetId: "",
    AMCStartDate: "",
    AMCEndDate: "",
    assetStatus: "Active",
    quantity: 1
  };
}

export function mapAssetFromApi(asset = {}) {
  const warrantyExpiry = toDateInputValue(
    asset.warrantyExpiry ||
      asset.warranty_expiry ||
      asset.warrantyEndDate ||
      asset.warranty_end_date
  );
  const amcStart = toDateInputValue(
    asset.AMCStartDate ||
      asset.amcStartDate ||
      asset.amc_start_date ||
      asset.AMC_Start_Date ||
      asset.amcstartDate
  );
  const amcEnd = toDateInputValue(
    asset.AMCEndDate ||
      asset.amcEndDate ||
      asset.amc_end_date ||
      asset.AMC_End_Date ||
      asset.amcendDate
  );
  const amcOrderDate = toDateInputValue(
    asset.amcOrderDate ||
      asset.amc_order_date ||
      asset.AMCOrderDate ||
      asset.AMC_Order_Date ||
      asset.amcorderDate
  );
  const locationId = asset.locationId || asset.location_id || "";
  const customerId = asset.customerId || asset.customer_id || "";

  return {
    ...asset,
    id: assetRecordId(asset),
    assetId: asset.assetId || asset.asset_id || asset.id,
    customerId: customerId ? String(customerId) : "",
    locationId: locationId ? String(locationId) : "",
    locationName: asset.locationName || asset.location_name || asset.location?.locationName || "",
    assetName: asset.assetName || asset.asset_name || "",
    assetCategory: asset.assetCategory || asset.asset_category || "",
    manufacturer: asset.manufacturer || "",
    modelNumber: asset.modelNumber || asset.model_number || "",
    serialNumber: asset.serialNumber || asset.serial_number || "",
    invoiceNumber: asset.invoiceNumber ?? asset.invoice_number ?? "",
    invoiceDate: toDateInputValue(asset.invoiceDate ?? asset.invoice_date),
    installDate: toDateInputValue(asset.installDate || asset.install_date),
    warrantyType: asset.warrantyType || asset.warranty_type || "",
    warrantyStartDate: toDateInputValue(asset.warrantyStartDate || asset.warranty_start_date),
    warrantyExpiry,
    warrantyEndDate: warrantyExpiry,
    amcType: toAmcTypeDisplay(asset.amcType || asset.amc_type || asset.AMCType || asset.amctype || ""),
    amcOrder:
      asset.amcOrder ||
      asset.amc_order ||
      asset.AMCOrder ||
      asset.amcOrderNo ||
      asset.amc_order_no ||
      asset.amcorderNo ||
      "",
    amcOrderDate,
    tenantAssetId: asset.tenantAssetId || asset.tenant_asset_id || asset.TenantAssetId || "",
    AMCStartDate: amcStart,
    AMCEndDate: amcEnd,
    assetStatus: (() => {
      const raw = asset.assetStatus || asset.asset_status || "Active";
      if (String(raw).toLowerCase() === "inactive" || raw === "InActive") return "Inactive";
      return raw;
    })(),
    quantity: extractAssetQuantity(asset) ?? 1
  };
}

export function buildAssetPayload(customerId, asset) {
  const noAmc = isNoAmcType(asset);
  const warrantyEnd = asset.warrantyExpiry || asset.warrantyEndDate || "";
  return {
    customerId: Number(customerId),
    locationId: Number(asset.locationId),
    assetName: asset.assetName || "",
    assetCategory: asset.assetCategory || "",
    manufacturer: asset.manufacturer || "",
    modelNumber: asset.modelNumber || "",
    serialNumber: asset.serialNumber || "",
    invoiceNumber: asset.invoiceNumber ?? "",
    invoiceDate: asset.invoiceDate || null,
    installDate: asset.installDate || "",
    warrantyType: asset.warrantyType || "",
    warrantyStartDate: asset.warrantyStartDate || "",
    warrantyExpiry: warrantyEnd,
    warrantyEndDate: warrantyEnd,
    amcType: asset.amcType || "",
    amcOrder: noAmc ? "" : (asset.amcOrder || ""),
    amcOrderDate: noAmc ? "" : (asset.amcOrderDate || ""),
    tenantAssetId: asset.tenantAssetId || "",
    AMCStartDate: noAmc ? "" : (asset.AMCStartDate || ""),
    AMCEndDate: noAmc ? "" : (asset.AMCEndDate || ""),
    assetStatus: asset.assetStatus || "Active",
    quantity: extractAssetQuantity(asset) ?? (Number(asset.quantity) || 1)
  };
}

export function assetMissingRequired(asset) {
  const name = (asset?.assetName || "").trim();
  const category = (asset?.assetCategory || "").trim();
  const locationId = asset?.locationId;
  return !name || !category || !locationId;
}

export function dedupeCustomers(customers = []) {
  const seen = new Set();
  return customers.filter((customer) => {
    const id = String(customerRecordId(customer) || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function withSelectedCustomer(customers, selected) {
  const selectedId = String(customerRecordId(selected) || "");
  if (!selectedId) return customers;
  if (customers.some((customer) => String(customerRecordId(customer)) === selectedId)) return customers;
  return [selected, ...customers];
}

export async function fetchCustomerChoices(query = "") {
  const trimmed = String(query || "").trim();
  if (trimmed) {
    const res = await customerService.searchByName(trimmed, 0, 50, "companyName", "ASC");
    return dedupeCustomers(res?.customers || []);
  }

  const customers = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < 8) {
    const res = await customerService.getCustomers(page, 100, "companyName", "ASC");
    const batch = Array.isArray(res?.customers) ? res.customers : [];
    customers.push(...batch);
    totalPages = Number(res?.totalPages) || 1;
    if (batch.length === 0) break;
    page += 1;
  }
  return dedupeCustomers(customers);
}

export async function fetchCustomerLocations(customerId) {
  if (!customerId) return [];
  const res = await customerService.getLocationsByCustomer(customerId, 0, 100, "locationId", "ASC");
  return Array.isArray(res?.locations) ? res.locations : [];
}
